// Funciones puras para el diseño de grid de la etiqueta, con la misma
// lógica de ancho que una grilla web de 12 columnas: cada columna y cada
// celda mide de 1 a 12 en relación al espacio disponible. Si una celda mide
// 12, la siguiente pasa a la fila de abajo; si dos celdas miden 6 y 6,
// quedan una al lado de la otra. La etiqueta se divide primero en columnas
// (para poder poner algo en vertical al lado de lo demás); dentro de cada
// columna, las celdas se acomodan con la misma regla. No tocan Firebase ni
// UI — mismo criterio que bulkEditLogic.js/attributeLogic.js.

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const ANCHO_MAXIMO = 12;

// Valor especial de `content.src` para una celda de imagen: usa el logo de
// Dashport ya embebido en el proyecto, sin necesidad de subir nada.
export const LOGO_SENTINEL = "__dashportLogo";

function celdaVacia(overrides) {
  return {
    id: uid(),
    width: ANCHO_MAXIMO,
    fontSize: 9,
    orientation: "horizontal",
    content: { type: "text", value: "" },
    ...overrides,
  };
}

function columnaVacia(overrides) {
  return {
    id: uid(),
    width: ANCHO_MAXIMO,
    cells: [celdaVacia()],
    ...overrides,
  };
}

// Grid semilla que reproduce, en estructura, el diseño de etiqueta que ya
// existía antes de que esta vista tuviera un tab propio: nombre y número
// arriba (mitad y mitad), línea de modalidad/categoría en medio, logo
// rotado abajo — todo dentro de una sola columna de ancho completo. El
// contenido de cada celda es texto con variables entre corchetes, igual
// que en el editor de correos (ver attributeLogic.js: resolverValorAtributo).
export function defaultGrid() {
  return {
    columns: [
      columnaVacia({
        cells: [
          celdaVacia({ width: 6, fontSize: 9, content: { type: "text", value: "[nombreCompleto]" } }),
          celdaVacia({ width: 6, fontSize: 16, content: { type: "text", value: "[numero]" } }),
          celdaVacia({
            width: 12,
            fontSize: 9,
            content: { type: "text", value: "[modalidad] - [rama] - [categoria]" },
          }),
          celdaVacia({
            width: 12,
            orientation: "vertical",
            content: { type: "image", src: LOGO_SENTINEL },
          }),
        ],
      }),
    ],
  };
}

// Acomoda una lista de items (columnas o celdas) en filas, sumando su
// "width" hasta 12 — igual que un flex-wrap de 12 columnas en la web. La
// misma función se usa para calcular el layout en el editor, la vista
// previa y el PDF final, para que los tres coincidan siempre.
export function distribuirEnFilas(items, obtenerAncho) {
  const filas = [];
  let filaActual = [];
  let acumulado = 0;
  (items || []).forEach((item) => {
    const w = obtenerAncho(item) || ANCHO_MAXIMO;
    if (acumulado + w > ANCHO_MAXIMO && filaActual.length > 0) {
      filas.push(filaActual);
      filaActual = [];
      acumulado = 0;
    }
    filaActual.push(item);
    acumulado += w;
    if (acumulado >= ANCHO_MAXIMO) {
      filas.push(filaActual);
      filaActual = [];
      acumulado = 0;
    }
  });
  if (filaActual.length > 0) filas.push(filaActual);
  return filas;
}

export function addColumn(grid, afterColumnIndex) {
  const columns = grid.columns.slice();
  const indice = afterColumnIndex === undefined ? columns.length : afterColumnIndex + 1;
  columns.splice(indice, 0, columnaVacia());
  return { ...grid, columns };
}

export function removeColumn(grid, columnIndex) {
  if (grid.columns.length <= 1) return grid; // siempre debe quedar al menos una columna
  return { ...grid, columns: grid.columns.filter((_, i) => i !== columnIndex) };
}

export function updateColumnWidth(grid, columnIndex, width) {
  const anchoValido = Math.min(ANCHO_MAXIMO, Math.max(1, Number(width) || 1));
  const columns = grid.columns.map((col, i) => (i === columnIndex ? { ...col, width: anchoValido } : col));
  return { ...grid, columns };
}

export function addCell(grid, columnIndex, afterCellId) {
  const columns = grid.columns.map((col, i) => {
    if (i !== columnIndex) return col;
    const cells = col.cells.slice();
    const indice = afterCellId ? cells.findIndex((c) => c.id === afterCellId) + 1 : cells.length;
    cells.splice(indice, 0, celdaVacia());
    return { ...col, cells };
  });
  return { ...grid, columns };
}

export function removeCell(grid, columnIndex, cellId) {
  const columns = grid.columns.map((col, i) => {
    if (i !== columnIndex) return col;
    if (col.cells.length <= 1) return col; // cada columna debe conservar al menos una celda
    return { ...col, cells: col.cells.filter((c) => c.id !== cellId) };
  });
  return { ...grid, columns };
}

export function updateCell(grid, columnIndex, cellId, patch) {
  const columns = grid.columns.map((col, i) => {
    if (i !== columnIndex) return col;
    const cells = col.cells.map((c) => {
      if (c.id !== cellId) return c;
      const actualizada = { ...c, ...patch };
      if (patch.width !== undefined) {
        actualizada.width = Math.min(ANCHO_MAXIMO, Math.max(1, Number(patch.width) || 1));
      }
      return actualizada;
    });
    return { ...col, cells };
  });
  return { ...grid, columns };
}
